import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  LocalOffer as LocalOfferIcon,
  AdsClick as AdsClickIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";
import pageOptions from "../../../constants/pageOptions";
import { useLocation } from "react-router-dom";

const API_BASE_URL = env("SERVER") + "/api";

const fetchOffers = async (page = 1, limit = 100, filters = {}) => {
  const accessToken = getCookie("accessToken");
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters,
  });
  const response = await axios.get(`${API_BASE_URL}/limited-offers?${params}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const fetchOfferActivity = async () => {
  const accessToken = getCookie("accessToken");
  const response = await axios.get(`${API_BASE_URL}/limited-offers/activity`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const createOffer = async (data) => {
  const accessToken = getCookie("accessToken");
  const cleanData = {
    name: data.name,
    title: data.title,
    pageName: data.pageName,
    ctaText: data.ctaText,
    ctaUrl: data.ctaUrl,
    endTime: data.endTime,
    accentColor: data.accentColor || "#286efb",
    isEnabled: data.isEnabled,
  };
  return await axios.post(`${API_BASE_URL}/limited-offers`, cleanData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const updateOffer = async (id, data) => {
  const accessToken = getCookie("accessToken");
  const cleanData = {
    name: data.name,
    title: data.title,
    pageName: data.pageName,
    ctaText: data.ctaText,
    ctaUrl: data.ctaUrl,
    endTime: data.endTime,
    accentColor: data.accentColor || "#286efb",
    isEnabled: data.isEnabled,
  };
  return await axios.put(`${API_BASE_URL}/limited-offers/${id}`, cleanData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const deleteOffer = async (id) => {
  const accessToken = getCookie("accessToken");
  return await axios.delete(`${API_BASE_URL}/limited-offers/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const toggleOfferStatus = async (id, isEnabled) => {
  const accessToken = getCookie("accessToken");
  return await axios.patch(
    `${API_BASE_URL}/limited-offers/${id}/toggle`,
    { isEnabled },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
};

const OfferForm = ({ open, onClose, offer, onSubmit, isEditing }) => {
  const theme = useTheme();
  const sevenDaysFromNow = () =>
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
  const [formData, setFormData] = useState({
    name: "New Offer",
    title: "Special Offer Closing In...",
    pageName: "",
    ctaText: "Click to Claim",
    ctaUrl: "https://clikkle.com/offers",
    endTime: sevenDaysFromNow(),
    accentColor: "#286efb",
    isEnabled: true,
  });

  useEffect(() => {
    if (offer) {
      setFormData({
        name: offer.name || "New Offer",
        title: offer.title || "Special Offer Closing In...",
        pageName: offer.pageName || "",
        ctaText: offer.ctaText || "Click to Claim",
        ctaUrl: offer.ctaUrl || "https://clikkle.com/offers",
        endTime: offer.endTime
          ? new Date(offer.endTime).toISOString().slice(0, 16)
          : "",
        accentColor: offer.accentColor || "#286efb",
        isEnabled: offer.isEnabled ?? true,
      });
    } else {
      setFormData({
        name: "New Offer",
        title: "Special Offer Closing In...",
        pageName: "",
        ctaText: "Click to Claim",
        ctaUrl: "https://clikkle.com/offers",
        endTime: sevenDaysFromNow(),
        accentColor: "#286efb",
        isEnabled: true,
      });
    }
  }, [offer, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      endTime: formData.endTime
        ? new Date(formData.endTime).toISOString()
        : null,
    };
    onSubmit(payload);
  };

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? "Edit Offer" : "Create New Offer"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Offer Name"
                value={formData.name}
                onChange={handleChange("name")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bar Title"
                value={formData.title}
                onChange={handleChange("title")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Page Name</InputLabel>
                <Select
                  value={formData.pageName}
                  onChange={handleChange("pageName")}
                  required
                >
                  {pageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CTA Text"
                value={formData.ctaText}
                onChange={handleChange("ctaText")}
                placeholder="e.g., Click to claim $37 Lifetime Deal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CTA URL"
                value={formData.ctaUrl}
                onChange={handleChange("ctaUrl")}
                placeholder="https://example.com/deal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={formData.endTime}
                onChange={handleChange("endTime")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Accent Color"
                type="color"
                value={formData.accentColor}
                onChange={handleChange("accentColor")}
                InputLabelProps={{ shrink: true }}
                sx={{ height: 56 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isEnabled}
                    onChange={handleChange("isEnabled")}
                  />
                }
                label="Enable Offer"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Offers = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Handle direct "New" action from Navbar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "new") {
      handleCreate();
    }
  }, [location.search]);

  const {
    data: offersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["limitedOffers", page, limit],
    queryFn: () => fetchOffers(page, limit),
    keepPreviousData: true,
  });

  const { data: activityData } = useQuery({
    queryKey: ["limitedOfferActivity"],
    queryFn: fetchOfferActivity,
    refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: createOffer,
    onSuccess: () => {
      queryClient.invalidateQueries(["limitedOffers"]);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Offer created successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating offer: ${error.message}`,
        severity: "error",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateOffer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["limitedOffers"]);
      setDialogOpen(false);
      setEditingOffer(null);
      setSnackbar({
        open: true,
        message: "Offer updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating offer: ${error.message}`,
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => {
      queryClient.invalidateQueries(["limitedOffers"]);
      setSnackbar({
        open: true,
        message: "Offer deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting offer: ${error.message}`,
        severity: "error",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }) => toggleOfferStatus(id, !isEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries(["limitedOffers"]);
      setSnackbar({
        open: true,
        message: "Offer status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating status: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleCreate = () => {
    setEditingOffer(null);
    setDialogOpen(true);
  };
  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setDialogOpen(true);
  };
  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };
  const handleToggle = (id) => {
    const offer = offersData?.data?.find((a) => a._id === id);
    if (offer) toggleMutation.mutate({ id, isEnabled: offer.isEnabled });
  };
  const handleSubmit = (formData) => {
    if (editingOffer)
      updateMutation.mutate({ id: editingOffer._id, data: formData });
    else createMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading offers: {error.message}
      </Alert>
    );
  }

  const mergedData = offersData?.data?.map((offer) => {
    const match = activityData?.data?.find(
      (a) => String(a.offerId) === String(offer._id)
    );
    return {
      ...offer,
      viewsCount: match?.viewsCount || 0,
      clicksCount: match?.clicksCount || 0,
      lastViewedAt: match?.lastViewedAt || null,
      lastClickedAt: match?.lastClickedAt || null,
    };
  });

  const totalClicks = activityData?.data?.reduce((sum, item) => sum + (item.clicksCount || 0), 0) || 0;
  const totalViews = activityData?.data?.reduce((sum, item) => sum + (item.viewsCount || 0), 0) || 0;
  const totalOffers = offersData?.pagination?.total || offersData?.data?.length || 0;
  const totalActive = offersData?.data?.filter(o => o.isEnabled).length || 0;
  const totalInactive = offersData?.data?.filter(o => !o.isEnabled).length || 0;
  const lastClicked = activityData?.data?.reduce((latest, item) => {
    if (!item.lastClickedAt) return latest;
    const current = new Date(item.lastClickedAt);
    return !latest || current > latest ? current : latest;
  }, null);

  return (
    <Box
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(4),
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={"bold"} gutterBottom>
            Limited Offers Bar Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage bottom bar limited-time offers for Clikkle Website.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size={isMobile ? "small" : "medium"}
        >
          Create Offer
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AdsClickIcon color="primary" />
                <Box>
                  <Typography variant="h6">{totalClicks}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Click</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AnalyticsIcon color="info" />
                <Box>
                  <Typography variant="h6">{totalViews}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Views</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <LocalOfferIcon color="success" />
                <Box>
                  <Typography variant="h6">{totalOffers}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="h6">{totalActive}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Active</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <VisibilityOffIcon color="warning" />
                <Box>
                  <Typography variant="h6">{totalInactive}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Inactive</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ backgroundColor: "transparent", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ScheduleIcon color="secondary" />
                <Box>
                  <Typography variant="h6" >
                    {lastClicked ? lastClicked.toLocaleDateString() : 'Never'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Last Clicked</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Page</TableCell>
                  <TableCell>CTA</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Clicks</TableCell>
                  <TableCell>Last Viewed</TableCell>
                  <TableCell>Last Clicked</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mergedData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No offers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mergedData?.map((offer) => (
                    <TableRow key={offer._id}>
                      <TableCell>{offer.name}</TableCell>
                      <TableCell>{offer.title}</TableCell>
                      <TableCell>{offer.pageName || "Home"}</TableCell>
                      <TableCell>
                        {offer.ctaText ? `${offer.ctaText}` : "-"}
                      </TableCell>
                      <TableCell>
                        {offer.endTime
                          ? new Date(offer.endTime).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>{offer.viewsCount}</TableCell>
                      <TableCell>{offer.clicksCount}</TableCell>
                      <TableCell>
                        {offer.lastViewedAt
                          ? new Date(offer.lastViewedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {offer.lastClickedAt
                          ? new Date(offer.lastClickedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.isEnabled ? "Active" : "Inactive"}
                          size="small"
                          color={offer.isEnabled ? "success" : "default"}
                          variant={offer.isEnabled ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(offer._id)}
                            color={offer.isEnabled ? "success" : "default"}
                          >
                            {offer.isEnabled ? (
                              <ViewIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(offer)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(offer._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <OfferForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingOffer(null);
        }}
        offer={editingOffer}
        onSubmit={handleSubmit}
        isEditing={!!editingOffer}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Offers;
